/**
 * api-read-cache — navigation-scoped GET cache for the shared axios client.
 *
 * Why this exists:
 *   ~240 components load their data in a `useEffect` through `apiRequest` /
 *   `apiClient`. None of them share a cache, so moving between pages — or
 *   between tabs of one page — re-downloads everything every time, and three
 *   components that each need `/workspaces` make three calls.
 *
 * What it does (no call site changes needed):
 *   1. In-flight de-duplication — identical concurrent GETs share ONE request.
 *   2. Navigation reuse — a GET answered in the last TTL_MS is served from
 *      memory, but ONLY during the first MOUNT_WINDOW_MS after a route change
 *      and before the user has clicked or typed anything on the new route.
 *
 * Rule 2 is what keeps it safe. Everything that must be live stays live
 * without any code knowing about this cache:
 *   - Polling (`setInterval`, `refetchInterval`) fires after the mount window
 *     on the same route → always goes to the network.
 *   - A Refresh / Try again / Retry button is a user click → closes the
 *     window → always goes to the network.
 *   - Any successful write (POST/PUT/PATCH/DELETE) clears the whole cache, so
 *     a page you return to after creating/renaming/deleting shows the change.
 *   - The cache is memory-only (never localStorage) and keyed by user+tenant;
 *     a different signed-in identity never sees another's entries.
 *
 * Opt a single request out with `{ cache: false }` in its axios config.
 */
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Set false to bypass the navigation read cache for this request. */
    cache?: boolean;
  }
}

export const API_WRITE_EVENT = 'uzo:api-write';

const TTL_MS = 60_000;
const MOUNT_WINDOW_MS = 2_500;
const MAX_ENTRIES = 300;

// Never cache these: identity/session, liveness, streams, file bodies.
const NEVER_CACHE = /\/(auth|csrf|ws-ticket|health|stream|sse|events|download|export)(\/|\?|$)|\/logs(\/|\?|$)/i;

interface Entry {
  t: number;
  status: number;
  statusText: string;
  headers: AxiosResponse['headers'];
  data: unknown;
}

const store = new Map<string, Entry>();
const inflight = new Map<string, Promise<AxiosResponse>>();

let routeKey = '';
let routeEnteredAt = 0;
let userActedAt = 0;
let installed = false;

function currentRoute(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname + window.location.search;
}

function onMaybeRouteChange() {
  const r = currentRoute();
  if (r !== routeKey) {
    routeKey = r;
    routeEnteredAt = Date.now();
    userActedAt = 0;
  }
}

function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  routeKey = currentRoute();
  routeEnteredAt = Date.now();
  for (const m of ['pushState', 'replaceState'] as const) {
    const orig = window.history[m];
    window.history[m] = function (this: History, ...args: Parameters<History['pushState']>) {
      const out = orig.apply(this, args);
      onMaybeRouteChange();
      return out;
    } as History['pushState'];
  }
  window.addEventListener('popstate', onMaybeRouteChange);
  // A click/keypress on the current route means "the user is now acting on
  // this page" — from here every read goes to the network (Refresh, Retry,
  // opening a panel). A click that navigates re-opens the window via the
  // history hook above, because pushState runs after pointerdown.
  const acted = () => { userActedAt = Date.now(); };
  window.addEventListener('pointerdown', acted, true);
  window.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') acted(); }, true);
}

function canServeFromCache(): boolean {
  onMaybeRouteChange();
  const now = Date.now();
  return userActedAt < routeEnteredAt && now - routeEnteredAt < MOUNT_WINDOW_MS;
}

function clone<T>(v: T): T {
  try { return structuredClone(v); } catch { return JSON.parse(JSON.stringify(v)); }
}

export function clearApiReadCache() {
  store.clear();
}

/**
 * Wrap an axios adapter with de-duplication + navigation reuse.
 * `identity` returns the signed-in tenant+user so entries never cross users.
 */
export function withReadCache(base: AxiosAdapter, identity: () => string): AxiosAdapter {
  install();
  return async (config: InternalAxiosRequestConfig) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get') {
      const res = await base(config);
      // A write changed server state; any cached read may now be wrong.
      store.clear();
      if (typeof window !== 'undefined' && res.status < 400) {
        // React Query caches (5 min, refetchOnMount:false) listen for this in
        // QueryProvider and mark their queries stale, so a list shows the
        // create/rename/delete the user just made.
        window.dispatchEvent(new CustomEvent(API_WRITE_EVENT, { detail: { url: config.url, method } }));
      }
      return res;
    }
    const url = `${config.baseURL || ''}|${config.url || ''}`;
    const cacheable =
      typeof window !== 'undefined' &&
      config.cache !== false &&
      (!config.responseType || config.responseType === 'json') &&
      !NEVER_CACHE.test(config.url || '');
    if (!cacheable) return base(config);

    const key = `${identity()}|${url}|${JSON.stringify(config.params ?? null)}`;

    const hit = store.get(key);
    if (hit && Date.now() - hit.t < TTL_MS && canServeFromCache()) {
      return {
        data: clone(hit.data), status: hit.status, statusText: hit.statusText,
        headers: hit.headers, config, request: { fromCache: true },
      } as AxiosResponse;
    }

    const pending = inflight.get(key);
    if (pending) {
      const res = await pending;
      return { ...res, data: clone(res.data), config };
    }

    const p = base(config).then((res) => {
      if (res.status >= 200 && res.status < 300) {
        if (store.size >= MAX_ENTRIES) store.delete(store.keys().next().value as string);
        store.set(key, { t: Date.now(), status: res.status, statusText: res.statusText, headers: res.headers, data: clone(res.data) });
      }
      return res;
    }).finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  };
}
